/**
 * What a scanned pickup code opens.
 *
 * The customer's QR carries a URL to this route, so a merchant can point the
 * phone's camera at an order and land here. It does not draw the order itself: it
 * resolves the token and hands it to the board, which opens it in the same sheet
 * a card opens. That keeps one description of an order and one home for its
 * actions — and it keeps following a link from being the act that hands the order
 * over, which is what a GET must never do.
 *
 * Sign-in is not bypassed. The app gates on the session before any route renders,
 * and because that gate is a render rather than a redirect, the URL survives it —
 * signing in lands here without any return-to plumbing.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState, ErrorBanner, LoadingState, Screen, useT } from '@yummy/ui-rnr';
import { ApiError, fetchOrderByPickupToken } from '@yummy/db';

import { supabase } from '../lib/supabase';

export function PickupScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!token) {
      setMissing(true);
      return;
    }

    let alive = true;
    void (async () => {
      try {
        const found = await fetchOrderByPickupToken(supabase, token);
        if (!alive) return;
        if (!found) {
          setMissing(true);
          return;
        }
        // Resolved here only so a code that names nothing gets its own answer
        // rather than a board that looks like it simply ignored the link. The
        // order itself is opened by the board, from the token.
        navigate(`/orders?open=${encodeURIComponent(token)}`, { replace: true });
      } catch (cause) {
        if (!alive) return;
        // A code belonging to another restaurant raises rather than answering
        // nothing — `merchant_order_by_token` refuses a token it does not own —
        // and "Not authorised for this restaurant" is a server's sentence in a
        // merchant's face. It is the same fact as a stale code, though: this
        // token names no order *here*. The not-found copy already says so, and
        // signing in as the wrong shop is the usual way to meet it.
        if (cause instanceof ApiError && cause.code === '42501') {
          setMissing(true);
          return;
        }
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, navigate]);

  if (error) {
    return (
      <Screen>
        <ErrorBanner message={error} onRetry={() => navigate('/orders')} />
      </Screen>
    );
  }

  // A token that names nothing is an ordinary thing to scan — a stale code, an
  // order from another restaurant, a screenshot kept past its use.
  if (missing) {
    return (
      <Screen>
        <EmptyState
          title={t('merchant.pickup.notFoundTitle')}
          message={t('merchant.pickup.notFound')}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <LoadingState message={t('merchant.pickup.loading')} />
    </Screen>
  );
}
